import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import NewComparison from "./pages/NewComparison";
import ComparisonHistory from "./pages/ComparisonHistory";
import ComparisonDetail from "./pages/ComparisonDetail";
import UserManagement from "./pages/UserManagement";
import RateSheetManagement from "./pages/RateSheetManagement";
import SettingsPage from "./pages/Settings";
import Analytics from "./pages/Analytics";
import CustomerPortal from "./pages/CustomerPortal";
import LeadsPage from "./pages/LeadsPage";
import RateLimitManagement from "./pages/RateLimitManagement";
import ContractGenerator from "./pages/ContractGenerator";
import AutomatedRenewals from "./pages/AutomatedRenewals";
import EmailToQuote from "./pages/EmailToQuote";
import CIDashboard from "./pages/CIDashboard";
import TariffOptimisation from "./pages/TariffOptimisation";
import CIRateCards from "./pages/CIRateCards";
import CIHistory from "./pages/CIHistory";
import LoadShape from "./pages/LoadShape";
import SMEMultisite from "./pages/SMEMultisite";
import "./App.css";

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<CustomerPortal />} />
          <Route path="/login" element={<Login />} />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/new-comparison" 
            element={
              <ProtectedRoute>
                <NewComparison />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/history" 
            element={
              <ProtectedRoute>
                <ComparisonHistory />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/comparison/:id" 
            element={
              <ProtectedRoute>
                <ComparisonDetail />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/users" 
            element={
              <ProtectedRoute adminOnly>
                <UserManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/rate-sheets" 
            element={
              <ProtectedRoute adminOnly>
                <RateSheetManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/settings" 
            element={
              <ProtectedRoute adminOnly>
                <SettingsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/analytics" 
            element={
              <ProtectedRoute>
                <Analytics />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/leads" 
            element={
              <ProtectedRoute adminOnly>
                <LeadsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/rate-limits" 
            element={
              <ProtectedRoute adminOnly>
                <RateLimitManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ci-dashboard" 
            element={
              <ProtectedRoute>
                <CIDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/contracts" 
            element={
              <ProtectedRoute>
                <ContractGenerator />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/renewals" 
            element={
              <ProtectedRoute>
                <AutomatedRenewals />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/email-to-quote" 
            element={
              <ProtectedRoute>
                <EmailToQuote />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tariff-optimisation" 
            element={
              <ProtectedRoute>
                <TariffOptimisation />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/load-shape" 
            element={
              <ProtectedRoute>
                <LoadShape />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/sme-multisite" 
            element={
              <ProtectedRoute>
                <SMEMultisite />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ci-history" 
            element={
              <ProtectedRoute>
                <CIHistory />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ci-rate-cards" 
            element={
              <ProtectedRoute adminOnly>
                <CIRateCards />
              </ProtectedRoute>
            } 
          />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
